import React, { useState } from 'react';
import {
  Typography,
  Box,
  Divider,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Paper,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { FiBell, FiX } from 'react-icons/fi';
import { FaFutbol } from 'react-icons/fa';
import { MatchData } from '@/interfaces/liveGamesType';
import { MatchDetailCard } from '../../styles';
import { isMatchLive } from '../../utils';
import AttackMomentumV2 from '../AttackMomentumV2/AttackMomentumV2';
import { useAuth } from '@/context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getMatchStats, getLiveOddsByMatch } from '../../services/api';
import MatchHeatmap from '../MatchHeatmap';
import FootballHeatmap from '../FootballHeatmap';
import PlayerAnalysis from '../PlayerAnalysis';
import HistoricalTrends from '../HistoricalTrends';
import MatchPredictions from '../MatchPredictions';

interface MatchDetailsProps {
  match: MatchData | null;
  onClose?: () => void;
  isMobile?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`match-tabpanel-${index}`}
      aria-labelledby={`match-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `match-tab-${index}`,
    'aria-controls': `match-tabpanel-${index}`,
  };
}

const MatchDetails: React.FC<MatchDetailsProps> = ({ match, onClose, isMobile = false }) => {
  const [tabValue, setTabValue] = useState(0);
  const { isAuthenticated } = useAuth();

  // Hooks devem ser chamados incondicionalmente
  const matchId = match?.match_id || '';
  const matchIsLive = match ? isMatchLive(match) : false;
  const refetchIntervalTime = matchIsLive ? 60000 : false; // 1 minuto para partidas ao vivo

  // Buscar estatísticas da partida - sempre chame os hooks, mesmo que não haja match
  const { data: matchStats, isLoading: statsLoading } = useQuery({
    queryKey: ['matchStats', matchId],
    queryFn: () => getMatchStats(matchId, isAuthenticated),
    enabled: !!matchId, // Só executa se tiver um matchId válido
    refetchInterval: refetchIntervalTime,
  });

  // Buscar odds da partida - sempre chame os hooks, mesmo que não haja match
  const { data: oddsData, isLoading: isLoadingOdds } = useQuery({
    queryKey: ['liveOdds', matchId],
    queryFn: () => getLiveOddsByMatch(matchId, isAuthenticated),
    enabled: !!matchId, // Só executa se tiver um matchId válido
    refetchInterval: refetchIntervalTime
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Renderizar mensagem quando não há partida selecionada
  if (!match) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="body1" color="textSecondary">
          Selecione uma partida para ver os detalhes
        </Typography>
      </Box>
    );
  }

  // Renderizar estatística com barra de progresso
  const renderStatWithProgress = (label: string, homeValue: string | number, awayValue: string | number) => {
    const homeNum = typeof homeValue === 'string' ? parseInt(homeValue, 10) || 0 : homeValue;
    const awayNum = typeof awayValue === 'string' ? parseInt(awayValue, 10) || 0 : awayValue;
    const total = homeNum + awayNum;
    const homePercent = total > 0 ? (homeNum / total) * 100 : 50;
    const awayPercent = total > 0 ? (awayNum / total) * 100 : 50;

    return (
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="body2" fontWeight="bold">{homeNum}</Typography>
          <Typography variant="body2">{label}</Typography>
          <Typography variant="body2" fontWeight="bold">{awayNum}</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Box sx={{ width: `${homePercent}%`, bgcolor: 'primary.main', height: 8, borderRadius: '4px 0 0 4px' }} />
          <Box sx={{ width: `${awayPercent}%`, bgcolor: 'error.main', height: 8, borderRadius: '0 4px 4px 0' }} />
        </Box>
      </Box>
    );
  };

  // Renderizar eventos da partida (gols, cartões)
  const renderMatchEvents = () => {
    const events = [];

    // Adicionar gols
    if (match.goalscorer && match.goalscorer.length > 0) {
      events.push(
        <Box key="goals" mb={2}>
          <Typography variant="subtitle2" gutterBottom>Gols</Typography>
          {match.goalscorer.map((goal, index) => (
            <Box key={`goal-${index}`} display="flex" alignItems="center" mb={1}>
              <FaFutbol style={{ marginRight: 8 }} />
              <Typography variant="body2">
                {goal.time}' - {goal.home_scorer || goal.away_scorer}
                {goal.home_assist || goal.away_assist ? ` (Assist: ${goal.home_assist || goal.away_assist})` : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    // Adicionar cartões
    if (match.cards && match.cards.length > 0) {
      events.push(
        <Box key="cards" mb={2}>
          <Typography variant="subtitle2" gutterBottom>Cartões</Typography>
          {match.cards.map((card, index) => (
            <Box key={`card-${index}`} display="flex" alignItems="center" mb={1}>
              <Box
                sx={{
                  width: 12,
                  height: 16,
                  bgcolor: card.card.toLowerCase().includes('yellow') ? 'yellow' : 'red',
                  mr: 1
                }}
              />
              <Typography variant="body2">
                {card.time}' - {card.home_fault || card.away_fault}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    if (events.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          Nenhum evento registrado
        </Typography>
      );
    }

    return events;
  };

  // Renderizar estatísticas da partida
  const renderMatchStatistics = () => {
    if (statsLoading) {
      return (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={30} />
        </Box>
      );
    }

    if (!matchStats || !match.statistics || match.statistics.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          Estatísticas não disponíveis para esta partida
        </Typography>
      );
    }

    // Estatísticas principais para exibir no topo
    const mainStats = ['Posse de Bola', 'Chutes a Gol', 'Chutes Fora', 'Escanteios', 'Faltas'];

    return (
      <>
        <Box mb={3}>
          {match.statistics.map((stat, index) => {
            const statName = stat.type;
            if (mainStats.includes(statName)) {
              return (
                <Box key={`stat-${index}`} mb={2}>
                  {renderStatWithProgress(statName, stat.home, stat.away)}
                </Box>
              );
            }
            return null;
          })}
        </Box>

        <Typography variant="subtitle2" gutterBottom>Estatísticas Detalhadas</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">{match.match_hometeam_name}</TableCell>
                <TableCell align="center">Estatística</TableCell>
                <TableCell align="center">{match.match_awayteam_name}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {match.statistics.map((stat, index) => (
                <TableRow key={`stat-detail-${index}`}>
                  <TableCell align="center">{stat.home}</TableCell>
                  <TableCell align="center">{stat.type}</TableCell>
                  <TableCell align="center">{stat.away}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  // Renderizar escalações
  const renderLineups = () => {
    if (!match.lineup || (!match.lineup.home && !match.lineup.away)) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          Escalações não disponíveis para esta partida
        </Typography>
      );
    }

    return (
      <Grid container spacing={2}>
        {/* Escalação do time da casa */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom align="center">
            {match.match_hometeam_name}
          </Typography>

          {match.lineup.home?.starting_lineups && match.lineup.home.starting_lineups.length > 0 ? (
            <>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Titulares
              </Typography>
              {match.lineup.home.starting_lineups.map((player, index) => (
                <Box key={`home-player-${index}`} display="flex" alignItems="center" mb={0.5}>
                  <Typography variant="body2" sx={{ minWidth: 24 }}>
                    {player.lineup_number}
                  </Typography>
                  <Typography variant="body2">
                    {player.lineup_player} ({player.lineup_position})
                  </Typography>
                </Box>
              ))}

              {match.lineup.home.substitutes && match.lineup.home.substitutes.length > 0 && (
                <>
                  <Typography variant="body2" fontWeight="bold" gutterBottom mt={2}>
                    Reservas
                  </Typography>
                  {match.lineup.home.substitutes.map((player, index) => (
                    <Box key={`home-sub-${index}`} display="flex" alignItems="center" mb={0.5}>
                      <Typography variant="body2" sx={{ minWidth: 24 }}>
                        {player.lineup_number}
                      </Typography>
                      <Typography variant="body2">
                        {player.lineup_player}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

              {match.lineup.home.coach && match.lineup.home.coach.length > 0 && (
                <Typography variant="body2" mt={1}>
                  <strong>Técnico:</strong> {match.lineup.home.coach[0]?.lineup_player}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              Escalação não disponível
            </Typography>
          )}
        </Grid>

        {/* Escalação do time visitante */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom align="center">
            {match.match_awayteam_name}
          </Typography>

          {match.lineup.away?.starting_lineups && match.lineup.away.starting_lineups.length > 0 ? (
            <>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Titulares
              </Typography>
              {match.lineup.away.starting_lineups.map((player, index) => (
                <Box key={`away-player-${index}`} display="flex" alignItems="center" mb={0.5}>
                  <Typography variant="body2" sx={{ minWidth: 24 }}>
                    {player.lineup_number}
                  </Typography>
                  <Typography variant="body2">
                    {player.lineup_player} ({player.lineup_position})
                  </Typography>
                </Box>
              ))}

              {match.lineup.away.substitutes && match.lineup.away.substitutes.length > 0 && (
                <>
                  <Typography variant="body2" fontWeight="bold" gutterBottom mt={2}>
                    Reservas
                  </Typography>
                  {match.lineup.away.substitutes.map((player, index) => (
                    <Box key={`away-sub-${index}`} display="flex" alignItems="center" mb={0.5}>
                      <Typography variant="body2" sx={{ minWidth: 24 }}>
                        {player.lineup_number}
                      </Typography>
                      <Typography variant="body2">
                        {player.lineup_player}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

              {match.lineup.away.coach && match.lineup.away.coach.length > 0 && (
                <Typography variant="body2" mt={1}>
                  <strong>Técnico:</strong> {match.lineup.away.coach[0]?.lineup_player}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              Escalação não disponível
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  };

  // Renderizar predições e tendências
  const renderPredictions = () => {
    return <MatchPredictions match={match} isLoading={false} />;
  };

  const getMatchStatusColor = () => {
    switch (match.match_status) {
      case 'IN PLAY':
        return 'success.main';
      case 'FINISHED':
        return 'text.secondary';
      case 'NOT STARTED':
        return 'warning.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <MatchDetailCard>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box gap={.5} display="flex" alignItems="center"> 
          <Chip
            label={match.league_name}
            size="small"
            variant="outlined"
            />
          <IconButton size="medium" aria-label="Receber notificações">
            <FiBell />
          </IconButton>
        </Box>
        <Box>
          {isMobile && onClose && (
            <IconButton 
              size="medium" 
              aria-label="Fechar detalhes" 
              onClick={onClose}
              sx={{ mr: 1 }}
            >
              <FiX />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Status da partida */}
      <Box textAlign="center" mb={2}>
        <Typography variant="body2" color="textSecondary">
          {matchIsLive
            ? `AO VIVO • ${match.match_status}`
            : `FINALIZADO • ${match.match_date}`
          }
        </Typography>
      </Box>

      {/* Times e placar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        {/* Time da Casa */}
        <Box display="flex" flexDirection="column" alignItems="center" width="40%">
          <Box
            component="img"
            src={match.team_home_badge}
            alt={match.match_hometeam_name}
            width={64}
            height={64}
            mb={1}
            onError={(e: any) => { e.target.src = 'https://placehold.co/64' }}
          />
          <Typography variant="body1" align="center">
            {match.match_hometeam_name}
          </Typography>
        </Box>

        {/* Placar */}
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography variant="h4" fontWeight="bold">
            {match.match_hometeam_score} - {match.match_awayteam_score}
          </Typography>
        </Box>

        {/* Time Visitante */}
        <Box display="flex" flexDirection="column" alignItems="center" width="40%">
          <Box
            component="img"
            src={match.team_away_badge}
            alt={match.match_awayteam_name}
            width={64}
            height={64}
            mb={1}
            onError={(e: any) => { e.target.src = 'https://placehold.co/64' }}
          />
          <Typography variant="body1" align="center">
            {match.match_awayteam_name}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Abas para diferentes seções de detalhe */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="match details tabs"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Resumo" {...a11yProps(0)} />
          <Tab label="Predições" {...a11yProps(1)} />
          <Tab label="Estatísticas" {...a11yProps(2)} />
          <Tab label="Escalações" {...a11yProps(3)} />
          <Tab label="Eventos" {...a11yProps(4)} />
        </Tabs>
      </Box>

      {/* Conteúdo das abas */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AttackMomentumV2 match={match} />
          </Grid>
          <Grid item xs={12}>
            <FootballHeatmap match={match} isLoading={false} />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderPredictions()}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderMatchStatistics()}
          </Grid>
          <Grid item xs={12}>
            <PlayerAnalysis match={match} isLoading={statsLoading} />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {renderLineups()}
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderMatchEvents()}
          </Grid>
          <Grid item xs={12}>
            <HistoricalTrends match={match} isLoading={false} />
          </Grid>
        </Grid>
      </TabPanel>
    </MatchDetailCard>
  );
};

export default MatchDetails;